import { Text } from "@chakra-ui/react";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Rectangle,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FORM_STATUS } from "src/constants";
import {
  GetDashboardFormStatusResponse,
  GetGradeConfigByEnrollmentPeriodResponse,
  GetSchoolsByEnrollmentPeriodResponse,
} from "src/generated/hooks/form-service/requests";
import { useGlossary } from "src/hooks/useGlossary";
import * as Format from "src/services/format";
import { isNonEmptyString } from "src/services/predicates";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { ChartTooltip } from "./ChartTooltip";
import { DashboardChart } from "./DashboardChart";
import { Grade, GradesMultiSelectInput } from "./GradesMultiSelectInput";
import { School, SchoolsMultiSelectInput } from "./SchoolsMultiSelectInput";
import { WrappedLabel } from "./WrappedLabel";

type Props = {
  formTemplateId: string;
  exportId?: string;
  remoteData: [
    RD.RemoteData<unknown, GetDashboardFormStatusResponse>,
    RD.RemoteData<unknown, GetSchoolsByEnrollmentPeriodResponse>,
    RD.RemoteData<unknown, GetGradeConfigByEnrollmentPeriodResponse>
  ];
};
const ASPECT_RATIO = 1.5;
export const ResponseByStatusChart: React.FC<Props> = ({
  formTemplateId,
  exportId,
  remoteData: [formStatusRD, schoolsRD, gradesRD],
}) => {
  const [selectedSchools, setSelectedSchools] = React.useState<School[] | null>(
    null
  );
  const [selectedGrades, setSelectedGrades] = React.useState<Grade[] | null>(
    null
  );

  const allSchools = React.useMemo(() => {
    if (schoolsRD.hasData()) {
      return schoolsRD.data.data;
    }
    return [];
  }, [schoolsRD]);

  const allGrades = React.useMemo(() => {
    if (gradesRD.hasData()) {
      return gradesRD.data.data;
    }
    return [];
  }, [gradesRD]);

  const { glossary, glossaryTransform } = useGlossary();

  const data = React.useMemo(() => {
    return formStatusRD.map((formStatus) => {
      return [
        {
          name: glossaryTransform(
            FORM_STATUS[GQL.form_status_enum.InProgress].label
          ),
          value: calculateStatusTotal(
            formTemplateId,
            "InProgress",
            formStatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#D9894F",
        },
        {
          name: glossaryTransform(
            FORM_STATUS[GQL.form_status_enum.Submitted].label
          ),
          value: calculateStatusTotal(
            formTemplateId,
            "Submitted",
            formStatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#38A169",
        },
        {
          name: glossaryTransform(
            FORM_STATUS[GQL.form_status_enum.Verified].label
          ),
          value: calculateStatusTotal(
            formTemplateId,
            "Verified",
            formStatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#00B5D8",
        },
        {
          name: glossaryTransform(
            FORM_STATUS[GQL.form_status_enum.LotteryReady].label
          ),
          value: calculateStatusTotal(
            formTemplateId,
            "LotteryReady",
            formStatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#805AD5",
        },
        {
          name: glossaryTransform(
            FORM_STATUS[GQL.form_status_enum.Admissions].label
          ),
          value: calculateStatusTotal(
            formTemplateId,
            "Admissions",
            formStatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#3182CE",
        },
        {
          name: glossaryTransform(
            FORM_STATUS[GQL.form_status_enum.Cancelled].label
          ),
          value: calculateStatusTotal(
            formTemplateId,
            "Cancelled",
            formStatus,
            selectedSchools,
            selectedGrades
          ),
          color: "#718096",
        },
      ];
    });
  }, [
    formStatusRD,
    glossaryTransform,
    formTemplateId,
    selectedSchools,
    selectedGrades,
  ]);

  return (
    <DashboardChart
      aspectRatio={ASPECT_RATIO}
      title={glossaryTransform("Responses by status")}
      data={data}
      exportId={exportId}
      caption={glossary`Form responses by school, not unique by student`}
      exportFileName="responses-by-status"
      filters={
        <>
          <SchoolsMultiSelectInput
            remoteData={schoolsRD}
            onChange={setSelectedSchools}
            selectedSchools={selectedSchools ?? allSchools}
          />
          <GradesMultiSelectInput
            remoteData={gradesRD}
            onChange={setSelectedGrades}
            selectedGrades={selectedGrades ?? allGrades}
          />
        </>
      }
    >
      {(data) => (
        <BarChart
          style={{
            width: "100%",
            aspectRatio: ASPECT_RATIO,
          }}
          responsive
          data={data}
          margin={{
            top: 24,
            right: 0,
            left: 0,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical />
          <XAxis
            dataKey="name"
            tickLine={false}
            axisLine={false}
            interval={0}
            tick={(props) => <WrappedLabel {...props} />}
          />
          <YAxis
            width={40}
            tickLine={false}
            axisLine={false}
            domain={[0, "auto"]}
            tickFormatter={(value: number) =>
              Intl.NumberFormat("en", { notation: "compact" }).format(value)
            }
          />
          <Tooltip<number, string>
            content={({ active, payload, label }) => {
              const value = payload[0]?.payload?.value;
              const color = payload[0]?.payload?.color;
              if (active && payload && value) {
                return (
                  <ChartTooltip
                    accentColor={color ?? ""}
                    title={String(label ?? "")}
                  >
                    <Text fontSize="0.9rem">
                      <strong>{Format.formatWholeNumber(value)}</strong>{" "}
                      {value === 1 ? "response" : "responses"}
                    </Text>
                  </ChartTooltip>
                );
              }
              return null;
            }}
          />
          <Bar
            dataKey="value"
            radius={[0, 0, 0, 0]}
            shape={(props: any) => (
              <Rectangle {...props} fill={props?.payload?.color ?? "#6894CA"} />
            )}
          >
            <LabelList dataKey="value" position="top" offset={8} />
          </Bar>
        </BarChart>
      )}
    </DashboardChart>
  );
};

export function calculateStatusTotal(
  formTemplateId: string,
  status: Status,
  formStatus: GetDashboardFormStatusResponse,
  selectedSchoolIds: School[] | null,
  selectedGradeIds: Grade[] | null
): number {
  let total = 0;
  for (const row of formStatus.data) {
    if (row.form_template_id !== formTemplateId || row.status !== status) {
      continue;
    }

    // null means "all selected" — skip that filter entirely
    // empty array means no schools selected — only include rows without a school
    if (selectedSchoolIds !== null) {
      if (isNonEmptyString(row.school_id)) {
        if (selectedSchoolIds.length === 0) {
          continue;
        }
        if (!selectedSchoolIds.find((s) => s.id === row.school_id)) {
          continue;
        }
      } else {
        if (selectedSchoolIds.length > 0) {
          continue;
        }
      }
    }

    if (selectedGradeIds !== null) {
      if (isNonEmptyString(row.grade_config_id)) {
        if (selectedGradeIds.length === 0) {
          continue;
        }
        if (!selectedGradeIds.find((g) => g.id === row.grade_config_id)) {
          continue;
        }
      } else {
        if (selectedGradeIds.length > 0) {
          continue;
        }
      }
    }

    let count = 0;
    if (row === undefined) continue;
    if (row.count === null) continue;
    if (typeof row.count === "number") {
      count = row.count;
    } else {
      count = parseInt(row.count);
    }
    total += count;
  }
  return total;
}

type Status = `${GQL.form_status_enum}`;
